import React, { useState, useEffect } from 'react';
import { Bell, BellOff } from 'lucide-react';
import { Alert, TriggerMode, CrossDirection, useAlertStore } from '../../store/alertStore';
import './AlertSettingsModal.css';

export interface AlertSettingsModalProps {
  alert: Alert;
  onClose: () => void;
}

const AlertSettingsModal: React.FC<AlertSettingsModalProps> = ({
  alert,
  onClose,
}) => {
  const { updateAlert, pauseAlert, resumeAlert, deleteAlert } = useAlertStore();

  const isPriceAlert = alert.alertType === 'price_above' || alert.alertType === 'price_below';
  const isVolumeSpikeAlert = alert.alertType === 'volume_spike';
  const isVolumeAboveAlert = alert.alertType === 'volume_above';
  const isVolumeAlert = isVolumeSpikeAlert || isVolumeAboveAlert;

  // Local state for form
  const [triggerMode, setTriggerMode] = useState<TriggerMode>(alert.triggerMode);
  const [crossDirection, setCrossDirection] = useState<CrossDirection | null>(
    alert.crossDirection || 'either'
  );
  const [volumeMultiplier, setVolumeMultiplier] = useState<number>(
    alert.volumeMultiplier || 2.0
  );
  const [volumeLookback, setVolumeLookback] = useState<number>(
    alert.volumeLookback || 10
  );
  const [volumeThreshold, setVolumeThreshold] = useState<number>(
    alert.volumeThreshold || 0
  );
  const [price, setPrice] = useState<number>(alert.price || 0);
  const [paused, setPaused] = useState<boolean>(alert.paused);
  const [activeSessions, setActiveSessions] = useState<string>(alert.activeSessions || 'all');

  // Track if form has been modified
  const [isModified, setIsModified] = useState(false);

  useEffect(() => {
    const hasChanges =
      triggerMode !== alert.triggerMode ||
      crossDirection !== alert.crossDirection ||
      volumeMultiplier !== alert.volumeMultiplier ||
      volumeLookback !== alert.volumeLookback ||
      volumeThreshold !== alert.volumeThreshold ||
      price !== alert.price ||
      paused !== alert.paused ||
      activeSessions !== (alert.activeSessions || 'all');
    setIsModified(hasChanges);
  }, [triggerMode, crossDirection, volumeMultiplier, volumeLookback, volumeThreshold, price, paused, activeSessions, alert]);

  const handleSave = async () => {
    const updates: Record<string, unknown> = {};

    if (triggerMode !== alert.triggerMode) {
      updates.triggerMode = triggerMode;
    }
    if (crossDirection !== alert.crossDirection) {
      updates.crossDirection = crossDirection;
    }
    if (isPriceAlert && price !== alert.price) {
      updates.price = price;
    }
    if (isVolumeSpikeAlert) {
      if (volumeMultiplier !== alert.volumeMultiplier) {
        updates.volumeMultiplier = volumeMultiplier;
      }
      if (volumeLookback !== alert.volumeLookback) {
        updates.volumeLookback = volumeLookback;
      }
    }
    if (isVolumeAboveAlert && volumeThreshold !== alert.volumeThreshold) {
      updates.volumeThreshold = volumeThreshold;
    }
    if (paused !== alert.paused) {
      updates.paused = paused;
    }
    if (activeSessions !== (alert.activeSessions || 'all')) {
      updates.activeSessions = activeSessions;
    }

    if (Object.keys(updates).length > 0) {
      await updateAlert(alert.id, updates);
    }
    onClose();
  };

  const handleDelete = async () => {
    if (confirm('Delete this alert?')) {
      await deleteAlert(alert.id);
      onClose();
    }
  };

  const handleTogglePause = () => {
    setPaused(!paused);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && isModified) {
      handleSave();
    }
  };

  const getAlertTypeLabel = () => {
    switch (alert.alertType) {
      case 'price_above':
        return 'Price Above';
      case 'price_below':
        return 'Price Below';
      case 'volume_spike':
        return 'Volume Spike';
      case 'volume_above':
        return 'Volume Above';
      default:
        return 'Alert';
    }
  };

  return (
    <div className="alert-settings-backdrop" onClick={onClose}>
      <div
        className="alert-settings-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="alert-settings-header">
          <h3>
            {getAlertTypeLabel()} Settings
            <span className="alert-symbol-badge">{alert.symbol}</span>
          </h3>
          <button className="alert-settings-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="alert-settings-content">
          {/* Price (for price alerts) */}
          {isPriceAlert && (
            <div className="alert-settings-section">
              <label className="alert-settings-label">Price Level</label>
              <div className="alert-settings-input-row">
                <input
                  type="number"
                  className="alert-settings-input price-input"
                  value={price}
                  onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                  step="0.25"
                />
              </div>
            </div>
          )}

          {/* Volume Spike Settings */}
          {isVolumeSpikeAlert && (
            <>
              <div className="alert-settings-section">
                <label className="alert-settings-label">Volume Multiplier</label>
                <div className="alert-settings-input-row">
                  <input
                    type="number"
                    className="alert-settings-input"
                    value={volumeMultiplier}
                    onChange={(e) =>
                      setVolumeMultiplier(parseFloat(e.target.value) || 1)
                    }
                    min="1"
                    max="10"
                    step="0.1"
                  />
                  <span className="alert-settings-suffix">x average</span>
                </div>
                <div className="alert-settings-hint">
                  Alert when volume exceeds {volumeMultiplier}x the average
                </div>
              </div>

              <div className="alert-settings-section">
                <label className="alert-settings-label">Lookback Period</label>
                <div className="alert-settings-input-row">
                  <input
                    type="number"
                    className="alert-settings-input"
                    value={volumeLookback}
                    onChange={(e) =>
                      setVolumeLookback(parseInt(e.target.value) || 10)
                    }
                    min="5"
                    max="100"
                  />
                  <span className="alert-settings-suffix">bars</span>
                </div>
                <div className="alert-settings-hint">
                  Number of bars to calculate average volume
                </div>
              </div>
            </>
          )}

          {/* Volume Above Settings (absolute threshold) */}
          {isVolumeAboveAlert && (
            <div className="alert-settings-section">
              <label className="alert-settings-label">Volume Threshold</label>
              <div className="alert-settings-input-row">
                <input
                  type="number"
                  className="alert-settings-input"
                  value={volumeThreshold}
                  onChange={(e) =>
                    setVolumeThreshold(parseFloat(e.target.value) || 0)
                  }
                  min="0"
                  step="100"
                />
                <span className="alert-settings-suffix">contracts</span>
              </div>
              <div className="alert-settings-hint">
                Alert when bar volume exceeds {Math.round(volumeThreshold).toLocaleString()} contracts
              </div>
            </div>
          )}

          {/* Trigger Mode */}
          <div className="alert-settings-section">
            <label className="alert-settings-label">Trigger Mode</label>
            <div className="alert-settings-options">
              <button
                className={`alert-option-btn ${triggerMode === 'once_per_minute' ? 'active' : ''}`}
                onClick={() => setTriggerMode('once_per_minute')}
              >
                <span className="option-icon">60s</span>
                <span className="option-text">Once per Minute</span>
              </button>
              <button
                className={`alert-option-btn ${triggerMode === 'once_per_bar' ? 'active' : ''}`}
                onClick={() => setTriggerMode('once_per_bar')}
              >
                <span className="option-icon">|</span>
                <span className="option-text">Once per Bar</span>
              </button>
              <button
                className={`alert-option-btn ${triggerMode === 'on_cross' ? 'active' : ''}`}
                onClick={() => setTriggerMode('on_cross')}
              >
                <span className="option-icon">×</span>
                <span className="option-text">On Cross Only</span>
              </button>
            </div>
            <div className="alert-settings-hint">
              {triggerMode === 'once_per_minute' &&
                'Alert at most once every 60 seconds while condition is met'}
              {triggerMode === 'once_per_bar' &&
                'Alert once per bar (uses current chart interval)'}
              {triggerMode === 'on_cross' &&
                'Alert only when price crosses the level'}
            </div>
          </div>

          {/* Cross Direction (for price alerts with on_cross mode) */}
          {isPriceAlert && triggerMode === 'on_cross' && (
            <div className="alert-settings-section">
              <label className="alert-settings-label">Cross Direction</label>
              <div className="alert-settings-options horizontal">
                <button
                  className={`alert-option-btn small ${crossDirection === 'from_below' ? 'active' : ''}`}
                  onClick={() => setCrossDirection('from_below')}
                >
                  <span className="option-icon">↑</span>
                  <span className="option-text">From Below</span>
                </button>
                <button
                  className={`alert-option-btn small ${crossDirection === 'from_above' ? 'active' : ''}`}
                  onClick={() => setCrossDirection('from_above')}
                >
                  <span className="option-icon">↓</span>
                  <span className="option-text">From Above</span>
                </button>
                <button
                  className={`alert-option-btn small ${crossDirection === 'either' ? 'active' : ''}`}
                  onClick={() => setCrossDirection('either')}
                >
                  <span className="option-icon">↕</span>
                  <span className="option-text">Either</span>
                </button>
              </div>
            </div>
          )}

          {/* Active Sessions (PST) */}
          <div className="alert-settings-section">
            <label className="alert-settings-label">Active Sessions (PST)</label>
            <div className="alert-settings-options horizontal" style={{ flexWrap: 'wrap' }}>
              {[
                { value: 'all', label: 'All', desc: '24/7' },
                { value: 'rth', label: 'RTH', desc: '6:30a-1p' },
                { value: 'premarket', label: 'Pre', desc: '1a-6:30a' },
                { value: 'postmarket', label: 'Post', desc: '1p-3p' },
                { value: 'overnight', label: 'Night', desc: '3p-1a' },
              ].map(s => {
                const sessions = activeSessions.split(',').map(x => x.trim());
                const isActive = activeSessions === 'all' ? s.value === 'all' : sessions.includes(s.value);
                return (
                  <button
                    key={s.value}
                    className={`alert-option-btn small ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      if (s.value === 'all') {
                        setActiveSessions('all');
                      } else {
                        let current = activeSessions === 'all' ? [] : activeSessions.split(',').map(x => x.trim());
                        if (current.includes(s.value)) {
                          current = current.filter(x => x !== s.value);
                        } else {
                          current.push(s.value);
                        }
                        setActiveSessions(current.length === 0 ? 'all' : current.join(','));
                      }
                    }}
                  >
                    <span className="option-text">{s.label}</span>
                    <span className="option-hint" style={{ fontSize: '9px', opacity: 0.6 }}>{s.desc}</span>
                  </button>
                );
              })}
            </div>
            <div className="alert-settings-hint">
              {activeSessions === 'all' ? 'Alert active during all sessions' : `Active during: ${activeSessions}`}
            </div>
          </div>

          {/* Pause/Resume Toggle */}
          <div className="alert-settings-section">
            <label className="alert-settings-label">Status</label>
            <button
              className={`alert-pause-toggle ${paused ? 'paused' : 'active'}`}
              onClick={handleTogglePause}
            >
              {paused ? (
                <>
                  <BellOff size={16} />
                  <span>Paused</span>
                </>
              ) : (
                <>
                  <Bell size={16} />
                  <span>Active</span>
                </>
              )}
            </button>
          </div>

          {/* Last Triggered Info */}
          {alert.lastTriggeredAt && (
            <div className="alert-settings-info">
              Last triggered:{' '}
              {new Date(alert.lastTriggeredAt).toLocaleString()}
            </div>
          )}
        </div>

        <div className="alert-settings-actions">
          <button className="alert-delete-btn" onClick={handleDelete}>
            Delete Alert
          </button>
          <div className="alert-settings-actions-right">
            <button className="alert-cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="alert-save-btn"
              onClick={handleSave}
              disabled={!isModified}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertSettingsModal;
